import React, { FunctionComponent, ReactNode, useContext, useEffect } from 'react'
import { TouchableOpacity } from 'react-native'

import { BlurView } from 'expo-blur'
import styled, { useTheme } from 'styled-components/native'

import { FLOATING_TAB_BAR_HEIGHT } from '@lyrahealth-inc/shared-app-logic/src/features/header/constants'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { AccessibilityRolesNative, IS_WEB } from '../../constants'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { TabBarContext } from '../tabBar/TabBarContext'

export { FLOATING_TAB_BAR_HEIGHT }

export const WEB_FLOATING_TAB_BAR_BOTTOM_SPACING = 16

export type FloatingTabBarTab = {
  key: string
  label: string
  icon: (isFocused: boolean, iconColor: string) => ReactNode
  onPress: () => void
  testID?: string
}

export type FloatingTabBarProps = {
  tabs: FloatingTabBarTab[]
  activeTabKey: string
  bottomInset?: number
  horizontalInset?: number
  useCenteredTabSpacing?: boolean
  testID?: string
}

const FloatingTabBarOuter = styled.View<{
  bottomInset: number
  horizontalInset: number
  theme: ThemeType
}>(({ bottomInset, horizontalInset, theme }) => ({
  position: IS_WEB ? 'fixed' : 'absolute',
  bottom: bottomInset + (IS_WEB ? WEB_FLOATING_TAB_BAR_BOTTOM_SPACING : 8),
  left: `${horizontalInset}px`,
  right: `${horizontalInset}px`,
  zIndex: IS_WEB ? 1 : undefined,
  borderRadius: 99,
  overflow: 'hidden',
  backgroundColor: theme.colors.components.floatingMobileNav.background,
}))

const FloatingTabItem = styled(TouchableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  flexBasis: 0,
  flexGrow: 1,
  minWidth: 44,
  maxWidth: 92,
  paddingVertical: theme.spacing['12px'],
}))

const ActiveIndicator = styled.View<{ backgroundColor: string }>(({ backgroundColor }) => ({
  position: 'absolute',
  top: 0,
  left: '50%',
  marginLeft: -22,
  width: 44,
  height: 2,
  backgroundColor,
}))

const StyledBlurView = styled(BlurView)<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingHorizontal: theme.spacing['24px'],
}))

const TabItemContent = styled.View({
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
})

export const FloatingTabBar: FunctionComponent<FloatingTabBarProps> = ({
  tabs,
  activeTabKey,
  bottomInset = 0,
  horizontalInset = 8,
  useCenteredTabSpacing = false,
  testID = 'FloatingTabBar',
}) => {
  const { colors, isDarkMode } = useTheme() as ThemeType
  const tabBarContext = useContext(TabBarContext)

  useEffect(() => {
    if (!IS_WEB || !tabBarContext) {
      return
    }
    const height = bottomInset + WEB_FLOATING_TAB_BAR_BOTTOM_SPACING + FLOATING_TAB_BAR_HEIGHT
    tabBarContext.setTabBarHeight(height)
    tabBarContext.onTabBarVisibleChange(true)
    return () => {
      tabBarContext.onTabBarVisibleChange(false)
    }
  }, [bottomInset, tabBarContext])

  const BLUR_INTENSITY = IS_WEB ? 20 : 10 // Reduce intensity for native to improve scroll performance

  return (
    <FloatingTabBarOuter
      bottomInset={bottomInset}
      horizontalInset={horizontalInset}
      accessibilityRole={AccessibilityRolesNative.TAB_LIST}
      testID={tID(testID)}
    >
      <StyledBlurView
        tint={isDarkMode ? 'dark' : 'light'}
        intensity={BLUR_INTENSITY}
        style={useCenteredTabSpacing && tabs.length < 3 ? { justifyContent: 'center', gap: '62px' } : undefined}
      >
        {tabs.map((tab) => {
          const isFocused = tab.key === activeTabKey

          const iconColor = isFocused
            ? colors.components.floatingMobileNav.iconActive
            : colors.components.floatingMobileNav.iconDefault

          return (
            <FloatingTabItem
              key={tab.key}
              activeOpacity={1}
              accessibilityRole={AccessibilityRolesNative.TAB}
              accessibilityState={isFocused ? { selected: true } : {}}
              accessibilityLabel={tab.label}
              onPress={tab.onPress}
              testID={tID(tab.testID ?? `${testID}-tab-${tab.key}`)}
            >
              {isFocused && <ActiveIndicator backgroundColor={colors.components.floatingMobileNav.activeIndicator} />}
              <TabItemContent>
                {tab.icon(isFocused, iconColor)}
                <BodyText
                  size={BodyTextSize.CAPTION}
                  color={iconColor}
                  text={tab.label}
                  medium={isFocused}
                  regular={!isFocused}
                  numberOfLines={1}
                />
              </TabItemContent>
            </FloatingTabItem>
          )
        })}
      </StyledBlurView>
    </FloatingTabBarOuter>
  )
}
